import { client } from '../apiClient';
import { LogUploadResponse } from '../../types/bot-sources/LogUploadResponse';
import { LogUpload } from '../../types/bot-sources/LogUpload';

const CSV_ENDPOINT = '/bot-sources/logs';

export const uploadLog = async (projectId: number, file: File): Promise<LogUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('projectId', String(projectId));

  // A 100 MB log file parsed synchronously on the server (with the inline
  // raw → hourly aggregation step at the end) can run well over axios's default
  // 120s. Pick a ceiling that won't trip on legitimate large uploads.
  const { data } = await client(false, true).post<LogUploadResponse>(
    `${CSV_ENDPOINT}/ingest`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 5 * 60 * 1000,
    }
  );
  return data;
};

export const getLogUploads = async (projectId: number): Promise<LogUpload[]> => {
  // Backend response shape is `{ uploads: LogUpload[] }` (named key, see
  // LogUploadController.getUploads). Unwrap here so callers/hooks can treat
  // the result as a plain array — without this, `setQueryData((old = []) => [..., ...old])`
  // in `useUploadLog` throws "old is not iterable" the moment the cache contains
  // a real fetched value (because `old` is the wrapper object, not the array).
  const { data } = await client(true, true).get<{ uploads: LogUpload[] }>(
    `${CSV_ENDPOINT}/uploads`,
    { params: { projectId } }
  );
  return data.uploads ?? [];
};

export const getLogUpload = async (uploadId: number): Promise<LogUpload> => {
  // Backend wraps the single record under `{ upload: ... }` (see
  // LogUploadController.getUpload) — mirror the list endpoint by unwrapping here.
  const { data } = await client(true, true).get<{ upload: LogUpload }>(
    `${CSV_ENDPOINT}/uploads/${uploadId}`
  );
  return data.upload;
};

export interface ClearLogDataResponse {
  eventsDeleted: number;
  rawDeleted: number;
  uploadsDeleted: number;
}

/**
 * Wipes every CSV-derived crawl record for a project (uploads + raw + hourly
 * rollups). Used by the "Clear CSV data" button on the Connect page to free
 * up the project's one-source-per-project slot so Cloudflare can be connected.
 */
export const clearLogData = async (projectId: number): Promise<ClearLogDataResponse> => {
  const { data } = await client(true, true).delete<ClearLogDataResponse>(CSV_ENDPOINT, {
    params: { projectId },
  });
  return data;
};
